'use client';

import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FormField } from '@/sanity.types';
import { Form } from 'react-bootstrap';

interface FormFieldRenderProps {
  field: {
    _key: string;
  } & FormField;
}

export const FormFieldRender: React.FC<FormFieldRenderProps> = ({ field }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  // Ensure fieldName exists and use it
  const fieldName = field.fieldName || field._key;

  const error = errors[fieldName];
  const errorMessage = error?.message as string | undefined;
  const isInvalid = !!error;

  const renderField = () => {
    switch (field.inputType) {
      case 'textArea':
        return (
          <Form.Control
            as="textarea"
            id={fieldName}
            {...register(fieldName)}
            placeholder={field.placeholder}
            isInvalid={isInvalid}
            rows={4}
            required={field.required}
          />
        );

      case 'select':
        return (
          <Form.Select
            id={fieldName}
            {...register(fieldName)}
            isInvalid={isInvalid}
            required={field.required}
          >
            <option value="">Select an option</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );

      case 'checkbox':
        // Handle multiple checkboxes case
        if (field.checkboxOptions && field.checkboxOptions.length > 1) {
          return (
            <Controller
              name={fieldName}
              control={control}
              defaultValue={[]}
              render={({ field: controllerField }) => {
                const selectedValues = controllerField.value || [];

                return (
                  <>
                    <div className="checkbox-group">
                      {field.checkboxOptions?.map((option) => {
                        const isChecked =
                          Array.isArray(selectedValues) &&
                          selectedValues.includes(option.value);

                        return (
                          <Form.Check
                            key={option.value}
                            type="checkbox"
                            id={`${fieldName}-${option.value}`}
                            label={option.label}
                            isInvalid={isInvalid}
                            checked={isChecked}
                            className="mb-2"
                            onChange={(e) => {
                              const updatedValues = [...(selectedValues || [])];
                              if (e.target.checked) {
                                if (!updatedValues.includes(option.value)) {
                                  updatedValues.push(option.value);
                                }
                              } else {
                                const index = updatedValues.indexOf(
                                  option.value
                                );
                                if (index !== -1) {
                                  updatedValues.splice(index, 1);
                                }
                              }
                              controllerField.onChange(updatedValues);
                            }}
                          />
                        );
                      })}
                    </div>
                    {isInvalid && (
                      <div className="text-danger mt-1 text-sm">
                        {errorMessage}
                      </div>
                    )}
                  </>
                );
              }}
            />
          );
        } else {
          // Single checkbox case
          return (
            <Form.Check
              type="checkbox"
              id={fieldName}
              label={field.fieldLabel}
              {...register(fieldName)}
              isInvalid={isInvalid}
              required={field.required}
            />
          );
        }

      case 'email':
        return (
          <Form.Control
            type="email"
            id={fieldName}
            {...register(fieldName)}
            placeholder={field.placeholder}
            isInvalid={isInvalid}
            required={field.required}
          />
        );

      case 'text':
      default:
        return (
          <Form.Control
            type="text"
            id={fieldName}
            {...register(fieldName)}
            placeholder={field.placeholder}
            isInvalid={isInvalid}
            required={field.required}
          />
        );
    }
  };

  // Function to render field-level error message
  const renderErrorMessage = () => {
    if (!errorMessage) return null;

    // For checkbox groups, error is handled within the Controller
    if (
      field.inputType === 'checkbox' &&
      field.checkboxOptions &&
      field.checkboxOptions.length > 1
    ) {
      return null;
    }

    return (
      <Form.Control.Feedback type="invalid">
        {errorMessage}
      </Form.Control.Feedback>
    );
  };

  return (
    <Form.Group className="mb-3">
      {field.inputType !== 'checkbox' ||
      (field?.checkboxOptions?.length && field?.checkboxOptions?.length > 1) ? (
        <Form.Label htmlFor={fieldName}>
          {field.fieldLabel}
          {field.required && <span className="text-danger ms-1">*</span>}
        </Form.Label>
      ) : null}

      {renderField()}
      {renderErrorMessage()}
    </Form.Group>
  );
};
